// Copyright (c) StrangeBee 2024

import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THMenu } from '@Molecules/THMenu/THMenu';
import { FormItemProps } from 'antd';
import React, { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { THButton } from '../THButton/THButton';
import { THDropdown } from '../THDropdown/THDropdown';
import AceEditor from 'react-ace';
import { modeType } from 'thehive-sdk';
import { Ace } from 'ace-builds';

import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/ext-searchbox';
import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/mode-jsx';
// require langs explicitly otherwise webpack will bundle everything
import 'ace-builds/src-noconflict/mode-xml';
import 'ace-builds/src-noconflict/snippets/xml';
import 'ace-builds/src-noconflict/mode-markdown';
import 'ace-builds/src-noconflict/snippets/markdown';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/snippets/json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/snippets/html';
import 'ace-builds/src-noconflict/mode-plain_text';
import 'ace-builds/src-noconflict/snippets/plain_text';

import './Helper.scss';
import './AceEditor.scss';

export interface TemplateHelpType {
    name: string;
    help?: string;
}

export const templates: TemplateHelpType[] = [
    {
        name: 'audit.requestId',
    },
    {
        name: 'audit.action',
    },
    {
        name: 'audit.mainAction',
    },
    {
        name: 'audit.objectId',
    },
    {
        name: 'audit.objectType',
    },
    {
        name: 'audit.details',
    },
    {
        name: 'audit._id',
    },
    {
        name: 'audit._label',
    },
    {
        name: 'audit._createdBy',
    },
    {
        name: 'audit._createdAt',
    },
    {
        name: 'user.login',
    },
    {
        name: 'user.name',
    },
    {
        name: 'user.email',
    },
    {
        name: 'user.locked',
    },
    {
        name: 'user.type',
    },
    {
        name: 'user._id',
    },
    {
        name: 'user._label',
    },
    {
        name: 'user._createdBy',
    },
    {
        name: 'user._createdAt',
    },
    {
        name: 'url',
    },
];

const languages: modeType[] = ['xml', 'markdown', 'json', 'html', 'plain_text'];

const formatListDisplay: Record<modeType, string> = {
    plain_text: 'plain text',
    json: 'JSON',
    markdown: 'markdown',
    html: 'HTML',
    xml: 'XML',
    javascript: 'Javascript',
};

interface HelperProps extends FormItemProps {
    borderless?: boolean;
    resize?: boolean;
    triggerElem?: ReactElement;
    name: string;
    valueFormat?: modeType;
    formatList?: modeType[];
    onChangeFormat?: (st: modeType) => void;
    placeholder?: string;
    extraFields?: TemplateHelpType[];
    hideHelper?: boolean;
    extraAction?: ReactNode;
    autoCompleter?: Ace.Completer;
    enableAutoCompletion?: boolean;
}

export const Helper = ({
    className,
    valueFormat,
    onChangeFormat,
    formatList = languages,
    triggerElem,
    placeholder,
    extraFields = [],
    hideHelper = false,
    extraAction,
    autoCompleter,
    enableAutoCompletion = false,
    ...props
}: HelperProps): ReactElement => {
    const i = useLocalMessage();
    const editorRef = useRef<AceEditor>(null);
    const [format, setFormat] = useState<modeType>(valueFormat || formatList[0] || 'plain_text');

    const templateVariables = useMemo(
        () => [...(extraFields || []), ...templates].sort((a, b) => a.name.localeCompare(b.name)),
        [extraFields],
    );

    useEffect(() => {
        const editor = editorRef.current?.editor;
        if (autoCompleter) {
            editor?.setOptions({ enableBasicAutocompletion: true, enableLiveAutocompletion: true });
            const previousCompleters = editor?.completers;
            editor?.completers.push(autoCompleter);
            return () => {
                if (editor && previousCompleters) editor.completers = previousCompleters;
            };
        }
    }, [autoCompleter]);

    useEffect(() => {
        onChangeFormat?.(format);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [format]);

    const Action = () => (
        <div className="template-helper">
            {valueFormat && (
                <THDropdown
                    triggerElem={() => (
                        <THButton
                            className="template-helper__menu-btn"
                            disabled={formatList.length <= 1}
                            type="link"
                        >{`Format "${formatListDisplay[format]}"`}</THButton>
                    )}
                    trigger={['click']}
                    menuClassName="template-helper__menu"
                    disabled={formatList.length <= 1}
                >
                    {formatList.length > 1
                        ? formatList.map((mode, index) => (
                              <THMenu.Item
                                  onClick={() => {
                                      setFormat(mode as modeType);
                                  }}
                                  key={`${index}-${formatListDisplay[mode]}`}
                              >
                                  <div className="template-helper__menu-item">
                                      <span className="template-helper__name">{formatListDisplay[mode]}</span>
                                  </div>
                              </THMenu.Item>
                          ))
                        : null}
                </THDropdown>
            )}

            {templateVariables ? (
                <THDropdown
                    triggerElem={() =>
                        triggerElem || (
                            <THButton className="template-helper__menu-btn" type="link">
                                {i('notification.notifiers.template.extra-action')}
                            </THButton>
                        )
                    }
                    trigger={['click']}
                    menuClassName="template-helper__menu"
                >
                    {templateVariables.map(({ name, help }) => (
                        <THMenu.Item
                            onClick={() => {
                                editorRef?.current?.editor.insert(`{{${name}}}`);
                            }}
                            key={name}
                        >
                            <div className="template-helper__menu-item">
                                <span className="template-helper__name">{name}</span>
                                {help && <span className="template-helper__help">{help}</span>}
                            </div>
                        </THMenu.Item>
                    ))}
                </THDropdown>
            ) : null}
        </div>
    );

    return (
        <THForm.Item<any> noStyle shouldUpdate={(prev, next) => prev[props.name] !== next[props.name]}>
            <THForm.Item
                className="th-text-area__helper"
                extraAction={extraAction || (!hideHelper && Action())}
                help={!hideHelper ? i('notification.notifiers.template.help') : undefined}
                {...props}
            >
                <AceEditor
                    width="100%"
                    height="15rem"
                    mode={format}
                    theme="tomorrow"
                    className={classList('th-text-area__helper', className)}
                    ref={editorRef}
                    placeholder={placeholder || i('notification.notifiers.template.placeholder')}
                    setOptions={{
                        enableBasicAutocompletion: enableAutoCompletion,
                        enableLiveAutocompletion: enableAutoCompletion,
                        enableSnippets: enableAutoCompletion,
                    }}
                    onLoad={(editor: Ace.Editor) => {
                        // @see https://github.com/securingsincity/react-ace/issues/616#issuecomment-477916985
                        editor.commands.removeCommand(editor.commands.byName.goToNextError);
                    }}
                />
            </THForm.Item>
        </THForm.Item>
    );
};
